// Services.js

import React from "react";
import SectionContainer from "../../components/SectionContainer";
import Reveal from "../../components/Reveal";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCogs,
  faChartLine,
  faMobileAlt,
  faCloud,
  faShieldAlt,
  faCode,
  faUser,
  faFilePdf,
  faDatabase,
  faArrowRight,
  faStar,
} from "@fortawesome/free-solid-svg-icons";

const iconMap = {
  Development: faCode,
  Analytics: faChartLine,
  Mobile: faMobileAlt,
  Cloud: faCloud,
  Security: faShieldAlt,
  Consulting: faUser,
  PDF: faFilePdf,
  Data: faDatabase,
};

const colorMap = {
  Development: "from-blue-500 to-indigo-600",
  Analytics: "from-green-500 to-emerald-600",
  Mobile: "from-purple-500 to-violet-600",
  Cloud: "from-cyan-500 to-blue-500",
  Security: "from-red-500 to-orange-600",
  Consulting: "from-yellow-500 to-orange-500",
  PDF: "from-pink-500 to-rose-600",
  Data: "from-teal-500 to-cyan-600",
};

const ServicesSection = ({ services }) => {
  return (
    <SectionContainer id="services" variant="subtle">
      <div className="relative">
        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-5 overflow-hidden">
          <svg className="w-full h-full" viewBox="0 0 100 100">
            <defs>
              <pattern
                id="services-grid"
                width="20"
                height="20"
                patternUnits="userSpaceOnUse"
              >
                <path
                  d="M 20 0 L 0 0 0 20"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="0.5"
                />
              </pattern>
            </defs>
            <rect width="100" height="100" fill="url(#services-grid)" />
          </svg>
        </div>

        {/* Header Section */}
        <div className="text-center mb-20 relative z-10">
          <div className="inline-flex items-center px-6 py-3 bg-[#E5C558]/10 rounded-full text-[#E5C558] font-semibold mb-6 border border-[#E5C558]/20">
            <FontAwesomeIcon icon={faStar} className="mr-2" />
            Premium Services
          </div>
          <h2 className="text-4xl lg:text-6xl font-bold text-gray-900 dark:text-white mb-8 leading-tight">
            Our{" "}
            <span className="bg-gradient-to-r from-[#E5C558] to-[#d4b54d] bg-clip-text text-transparent">
              Services
            </span>
          </h2>
          <p className="text-xl lg:text-2xl text-gray-600 dark:text-gray-300 max-w-4xl mx-auto leading-relaxed">
            Tailored solutions designed to help your business grow with modern
            technology, personal support, and startup-friendly pricing.
          </p>
        </div>

        {/* Services Grid */}
        <div className="grid gap-8 sm:grid-cols-2 lg:grid-cols-3 relative z-10">
          {services.map((service, index) => (
            <Reveal key={index} delay={index * 0.1}>
              <div className="group relative bg-white dark:bg-gray-800 rounded-3xl p-8 shadow-lg hover:shadow-2xl transition-all duration-500 border border-gray-100 dark:border-gray-700 overflow-hidden hover:-translate-y-2">
                {/* Background Gradient */}
                <div
                  className={`absolute top-0 right-0 w-32 h-32 bg-gradient-to-br ${
                    colorMap[service.icon] || "from-gray-400 to-gray-600"
                  } opacity-10 rounded-full transform translate-x-16 -translate-y-16 group-hover:opacity-20 transition-opacity duration-500`}
                ></div>

                {/* Icon */}
                <div
                  className={`w-16 h-16 rounded-2xl bg-gradient-to-br ${
                    colorMap[service.icon] || "from-gray-400 to-gray-600"
                  } flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300 shadow-lg`}
                >
                  <FontAwesomeIcon
                    icon={iconMap[service.icon] || faCogs}
                    className="text-white text-2xl"
                  />
                </div>

                {/* Content */}
                <div className="relative z-10">
                  <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-4 group-hover:text-[#E5C558] transition-colors duration-300">
                    {service.title}
                  </h3>
                  <p className="text-gray-600 dark:text-gray-400 leading-relaxed mb-8 line-clamp-4">
                    {service.description}
                  </p>

                  {/* Features List (if available) */}
                  {service.features && (
                    <div className="mb-6">
                      <ul className="space-y-2">
                        {service.features.slice(0, 3).map((feature, idx) => (
                          <li
                            key={idx}
                            className="flex items-center text-sm text-gray-600 dark:text-gray-400"
                          >
                            <div className="w-2 h-2 bg-[#E5C558] rounded-full mr-3 flex-shrink-0"></div>
                            {feature}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {/* CTA Button */}
                  <div className="mt-auto">
                    <a
                      href={`services/${service.url}`}
                      className="group/btn inline-flex items-center px-6 py-3 bg-gradient-to-r from-[#E5C558] to-[#d4b54d] text-white font-semibold rounded-full hover:shadow-lg hover:shadow-[#E5C558]/40 transition-all duration-300 hover:scale-105"
                    >
                      Learn More
                      <FontAwesomeIcon
                        icon={faArrowRight}
                        className="ml-2 text-sm group-hover/btn:translate-x-1 transition-transform duration-300"
                      />
                    </a>
                  </div>
                </div>

                {/* Popular Badge */}
                {index === 0 && (
                  <div className="absolute top-4 right-4 bg-gradient-to-r from-[#E5C558] to-[#d4b54d] text-white px-3 py-1 rounded-full text-xs font-semibold">
                    Popular
                  </div>
                )}
              </div>
            </Reveal>
          ))}
        </div>

        {/* CTA Section */}
        <div className="text-center mt-20 relative z-10">
          <div className="bg-gradient-to-r from-[#E5C558]/10 to-[#d4b54d]/10 rounded-3xl p-12 border border-[#E5C558]/20 backdrop-blur-sm">
            <h3 className="text-3xl font-bold text-gray-900 dark:text-white mb-4">
              Ready to Get Started?
            </h3>
            <p className="text-lg text-gray-600 dark:text-gray-300 mb-8 max-w-2xl mx-auto">
              Let's discuss your project and find the perfect solution for your
              business needs.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="/contact-us"
                className="inline-flex items-center px-8 py-4 bg-gradient-to-r from-[#E5C558] to-[#d4b54d] text-white font-bold rounded-full hover:shadow-xl transition-all duration-300 hover:scale-105"
              >
                Start Your Project
                <FontAwesomeIcon icon={faArrowRight} className="ml-2" />
              </a>
              <a
                href="/services"
                className="inline-flex items-center px-8 py-4 bg-transparent border-2 border-[#E5C558] text-[#E5C558] font-bold rounded-full hover:bg-[#E5C558] hover:text-white transition-all duration-300"
              >
                View All Services
              </a>
            </div>
          </div>
        </div>
      </div>
    </SectionContainer>
  );
};

export default ServicesSection;
